import axios from "axios";
import { MaterialReactTable, MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { useEffect, useState } from "react";
import { UrlUpdateType } from "use-query-params";
import ReviewRemovalRequestModal from "../../RemovalRequest/ReviewRemovalRequestModal";
import { PropertyRecord } from "../../types";
import { propertyRecordColumns } from "../PropertyRecordColumns";
import PropertyEntityHeader from "./PropertyEntityHeader";

interface PropertyRecordsTabProps {
  selectedEntityId?: number;
  setSelectedEntityId: (newValue: string | null | undefined, updateType?: UrlUpdateType) => void;
  onEdit?: (entityId: number) => void;
}

const PropertyRecordsTab = ({ selectedEntityId, setSelectedEntityId, onEdit }: PropertyRecordsTabProps) => {
  const [propertyRecords, setPropertyRecords] = useState<PropertyRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [selectedPropertyWithRemovalRequest, setSelectedPropertyWithRemovalRequest] = useState<PropertyRecord | null>(
    null
  );
  const [rowCount, setRowCount] = useState<number>(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const fetchPropertyRecords = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post("/api/blacklist/getPropertyRecordsPaginated", {
        pageSize: pagination.pageSize,
        pageNumber: pagination.pageIndex,
        searchTerm: globalFilter,
        sortField: sorting.length > 0 ? sorting[0].id : "",
        sortDescending: sorting.length > 0 ? sorting[0].desc : false
      });
      setPropertyRecords(data.data);
      setRowCount(data.count);
    } catch (error) {
      console.error("Error fetching property records", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyWithRemovalRequest = async (propertyId: number) => {
    setIsModalLoading(true);
    try {
      const { data } = await axios.get<PropertyRecord>(`/api/blacklist/GetPropertyRecordById?id=${propertyId}`);
      if (data.removalRequest) {
        setSelectedPropertyWithRemovalRequest(data);
      } else {
        setSelectedEntityId(undefined);
      }
    } catch (error) {
      console.error("Error fetching property with removal request", error);
    } finally {
      setIsModalLoading(false);
    }
  };

  useEffect(() => {
    fetchPropertyRecords();
  }, [pagination.pageIndex, pagination.pageSize, sorting, globalFilter]);

  useEffect(() => {
    if (!selectedEntityId) return;
    fetchPropertyWithRemovalRequest(selectedEntityId);
  }, [selectedEntityId]);

  const handleCloseModal = (): void => {
    setSelectedPropertyWithRemovalRequest(null);
    setSelectedEntityId(undefined);
  };

  const handleRefreshUpdatedRequest = async (): Promise<void> => {
    await fetchPropertyRecords();
  };

  return (
    <>
      <MaterialReactTable
        enableStickyHeader
        columns={propertyRecordColumns(fetchPropertyRecords, onEdit)}
        data={propertyRecords}
        initialState={{ columnPinning: { left: ['edit'] } }}
        manualPagination
        manualFiltering
        manualSorting
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        onGlobalFilterChange={setGlobalFilter}
        rowCount={rowCount}
        state={{
          isLoading: loading,
          pagination,
          sorting,
          globalFilter
        }}
        muiTableBodyRowProps={({ row }) => ({
          sx: { backgroundColor: row.index % 2 === 0 ? "#f4f4f4" : "white" }
        })}
      />

      {selectedPropertyWithRemovalRequest?.removalRequest && (
        <ReviewRemovalRequestModal
          open={Boolean(selectedPropertyWithRemovalRequest)}
          handleClose={handleCloseModal}
          removalRequest={selectedPropertyWithRemovalRequest.removalRequest}
          onActionComplete={handleRefreshUpdatedRequest}
          BlacklistDetailsComponent={<PropertyEntityHeader property={selectedPropertyWithRemovalRequest} />}
        />
      )}
    </>
  );
};

export default PropertyRecordsTab;
